import Debug from 'debug'
import { callUnaryMethod } from '@connectrpc/connect-query'
import type { MutationOptions } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { GetPathRequest_Sort } from '@jotta/grpc-connect-openapi/file'
import {
  delete$,
  deletePermanently,
  restore,
} from '@jotta/grpc-connect-openapi/fileQuery'
import {
  addBackgroundJob,
  type BackgroundJobStatus,
  getOngoingBackgroundJobs,
  type TrashActionType,
} from '@jotta/hooks/useBackgroundJobStore'
import { handleUnknownError } from '@jotta/types/AppError'
import { delay } from '@jotta/utils/delay'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { authTransport } from '../transport'

import { useGetPath } from './useGetPath'

type ToastStatus = Extract<
  BackgroundJobStatus,
  'IN_PROGRESS' | 'SUCCESS' | 'FAILED'
>

const debug = Debug('jotta:files:emptyTrash')

type UseFileTrashMutationProps = {
  paths: string[]
  actionType: TrashActionType
  toastId?: string | number | undefined
}

export const useFileTrashMutation = <T>(options?: {
  onMutate?: MutationOptions<any, any, UseFileTrashMutationProps, T>['onMutate']
  onSettled?: MutationOptions<
    any,
    any,
    UseFileTrashMutationProps,
    T
  >['onSettled']
}) =>
  useMutation({
    onMutate: options?.onMutate,
    onSettled: options?.onSettled,
    mutationFn: async ({ paths, actionType }: UseFileTrashMutationProps) => {
      const promises = paths.map(path => {
        const log = debug.extend(`${actionType}:${path}`)
        log('Start')

        const input = {
          path,
          supportsBackgroundJobs: true,
        }

        switch (actionType) {
          case 'DELETE_PERMANENTLY':
            return callUnaryMethod(authTransport, deletePermanently, input)
          case 'RESTORE':
            return callUnaryMethod(authTransport, restore, input)
          case 'MOVE_TO_TRASH':
            return callUnaryMethod(authTransport, delete$, input)
          default:
            return exhaustiveGuard(actionType)
        }
      })

      const results = await Promise.allSettled(promises)

      results
        .filter(result => result.status === 'fulfilled')
        .forEach(result => {
          const backgroundJobId = result.value.backgroundJob?.id
          if (backgroundJobId) {
            addBackgroundJob(actionType, backgroundJobId)
          }
        })

      const failed = results.filter(result => result.status === 'rejected')
      if (failed.length > 0) {
        // retain maximal context from first failure but override the message
        const appError = handleUnknownError(failed[0].reason)
        appError.message = `Operation failed for ${failed.length} items (${appError.message})`
        throw appError
      }

      while (getOngoingBackgroundJobs(actionType).length > 0) {
        await delay(1000)
      }
    },
  })

const processEmptyTrash = async ({
  timestamp,
  processItems,
}: {
  timestamp: number
  processItems: ReturnType<typeof useFileTrashMutation>['mutateAsync']
}) => {
  const limit = 100
  const getPathResponse = await useGetPath.fetch(
    {
      path: '/trash',
      limit,
      sort: GetPathRequest_Sort.DATE_ASC,
      includeDeleted: true,
      includeChildren: true,
      includeThumb: false,
    },
    { transport: authTransport },
  )

  const childrenList =
    getPathResponse?.pathItem?.children.filter(
      child => child.deletedAtMillis <= timestamp,
    ) || []

  debug(
    'Delete %d paths %s',
    childrenList.length,
    childrenList.map(item => item.name).join('\n'),
  )

  await processItems({
    actionType: 'DELETE_PERMANENTLY',
    paths: childrenList.map(child => child.path),
  })

  return Boolean(getPathResponse?.cursor && childrenList.length === limit)
}

export const emptyTrashMutationKey = ['emptyTrash']
type EmptyTrashMutationSignal = {
  isCancelled: boolean
}
export const useEmptyTrashMutation = <
  T extends string | number | undefined,
>(options?: {
  onMutate?: (variables: EmptyTrashMutationSignal) => T
  onSettled?: (
    status: ToastStatus,
    toastId: string | number | undefined,
    variables: EmptyTrashMutationSignal,
  ) => void
}) => {
  const emptyTrashMutation = useFileTrashMutation()

  return useMutation({
    mutationKey: emptyTrashMutationKey,
    mutationFn: async (variables: EmptyTrashMutationSignal) => {
      const timestamp = new Date().valueOf()
      let shouldContinue
      do {
        // Check for cancellation before processing batch
        if (variables.isCancelled) {
          debug('Empty trash operation was cancelled')
          return
        }

        // Process a batch of items
        shouldContinue = await processEmptyTrash({
          timestamp,
          processItems: async params => {
            if (!variables.isCancelled) {
              return await emptyTrashMutation.mutateAsync(params)
            }
          },
        })
      } while (shouldContinue)
    },
    onMutate: options?.onMutate,
    onSettled: (_, error, variables, context) => {
      options?.onSettled?.(error ? 'FAILED' : 'SUCCESS', context, variables)
    },
  })
}
